import { faMicrophone, faMicrophoneSlash } from '@fortawesome/pro-solid-svg-icons';
import { ComponentProps } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { cn } from '@growtherapy/sprout-ui';

import { ControlPanelButton, ControlPanelButtonTooltip } from '../../components/ControlPanelButton';
import { useTheme } from '../../hooks/useTheme';
import { Theme } from '../../state/theme';
import { VOLUME_INDICATOR_COUNT } from './constants';

export type ControlPanelButtonProps = Omit<
  ComponentProps<typeof ControlPanelButton>,
  'aria-label' | 'icon' | 'iconName' | 'iconVariant' | 'iconDefinition'
>;

type AudioControlProps = ControlPanelButtonProps & {
  isEnabled?: boolean;
  loading?: boolean;
  onToggle: (enable: boolean) => void;
  volume?: number;
};

export function AudioControl({ isEnabled, loading, onToggle, volume, ...props }: AudioControlProps) {
  const t = useTranslate();
  const fillPercentage = `${(((volume ?? 0) * 100) / VOLUME_INDICATOR_COUNT).toFixed(0)}%`;
  const { theme } = useTheme();
  const isDarkTheme = theme === Theme.DARK;

  const label = isEnabled ? t('telehealth-core:controls.toggleAudioOff') : t('telehealth-core:controls.toggleAudioOn');

  return (
    <ControlPanelButtonTooltip childIsInteractive={!props.disabled} text={label}>
      <ControlPanelButton
        {...props}
        aria-label={label}
        loading={loading}
        className={cn({
          'text-coral-600': !isEnabled,
          'rebrand:text-text-inverse': !isEnabled && isDarkTheme,
          'rebrand:text-status-error-text': !isEnabled && !isDarkTheme,
        })}
        iconDefinition={isEnabled ? faMicrophone : faMicrophoneSlash}
        data-testid='audio-control'
        onClick={() => onToggle(!isEnabled)}
        style={{
          // no-dd-sa:typescript-code-style/ban-ts-comment
          // @ts-ignore --volume-level is a custom property
          '--volume-level': fillPercentage,
          backgroundImage:
            'linear-gradient(to top, hsl(var(--twc-green-400)) 0%, hsl(var(--twc-green-400)) var(--volume-level), transparent var(--volume-level), transparent 100%)',
        }}
      />
    </ControlPanelButtonTooltip>
  );
}
